import {
  AvatarProps,
  createStyles,
  ListItem,
  makeStyles,
} from '@material-ui/core';
import classNames from 'classnames';
import { Avatar, Body } from 'copilot-design-system';
import React, { FC, memo } from 'react';

import { useNow } from 'src/hooks/useNow';
import { StatusIcon } from 'src/legacy/components/Icons/StatusIcon';
import BaseTypography, {
  typography13RegularStyle,
} from 'src/legacy/components/Text/BaseTypography';
import {
  BlackHeadings,
  GraySmall,
  HoverBackground,
  NonHoverBorder,
} from 'src/theme/colors';
import { getTimeFromNow } from 'src/utils/DateUtils';
import { getInitials } from 'src/utils/UserUtils';

import { AvatarGroup } from '../Avatars';
import { TruncatedText } from '../TruncatedText';

const CHANNEL_ITEM_HEIGHT = 68;
export type ChannelMemberData = {
  name: string;
  avatar: string;
  fallbackColor: string;
  subAvatarInfo?: ChannelMemberData;
};

type ChannelListItemStyleProps = Pick<
  ChannelListItemProps,
  'isNotification' | 'unread'
>;
interface ChannelListItemProps {
  title: string;
  subTitle: string;
  unread: boolean;
  onSelect: React.ComponentProps<typeof ListItem>['onSelect'];
  members?: ChannelMemberData[];
  avatarVariant?: AvatarProps['variant'];
  selected?: boolean;
  timestamp?: Date;
  // subTitleRenderer is a component that will be used to render the subtitle
  // text of the channel item. This is useful when we want to render the subtitle
  // along side some other component. For example, in the Inbox page we want to
  // show an avatar before the text. The allows the style of the text always be the same
  // in look and feel and the parent component can decide how to render anything
  // along side the text.
  subTitleRenderer?: React.FC;
  isNotification?: boolean;
}

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      width: '100%',
      height: CHANNEL_ITEM_HEIGHT,
      padding: theme.spacing(1.5, 2.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: `1px solid ${NonHoverBorder}`,
      // hover state on desktop
      [theme.breakpoints.up('sm')]: {
        '&:hover': {
          backgroundColor: HoverBackground,
          cursor: 'pointer',
        },
      },
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
    },
    summarySlot: {
      display: 'flex',
      gap: (props: ChannelListItemStyleProps) =>
        !props.isNotification ? theme.spacing(1.5) : 0,
      alignItems: 'center',
      width: '100%',
    },
    titleSlot: {
      display: 'flex',
      flexDirection: 'column',
      width: (props: ChannelListItemStyleProps) =>
        props.isNotification ? 300 : 245,
      maxWidth: (props: ChannelListItemStyleProps) =>
        props.isNotification ? 300 : 245,
      justifyContent: 'center',
    },
    time: {
      height: '100%',
      boxSizing: 'content-box',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      justifyContent: (props: ChannelListItemStyleProps) =>
        props.unread ? 'space-between' : 'flex-end',
    },
    selected: {
      [theme.breakpoints.up('sm')]: {
        backgroundColor: HoverBackground,
      },
    },
    status: {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    },
  }),
);

const ChannelTimeStamp: FC<{ time: Date; unReadNotification: boolean }> = memo(
  ({ time, unReadNotification }) => {
    const { now: nowTime } = useNow();
    const humanizedTimeSince = getTimeFromNow(time, nowTime);

    return (
      <BaseTypography
        textColor={GraySmall}
        fontType={unReadNotification ? '13Medium' : '13Regular'}
      >
        {humanizedTimeSince}
      </BaseTypography>
    );
  },
);

export const DefaultSubTitleRenderer: FC = ({ children }) => <>{children}</>;

export const ChannelListItem: FC<ChannelListItemProps> = ({
  members,
  avatarVariant = 'rounded',
  title,
  subTitle,
  subTitleRenderer,
  unread,
  timestamp,
  onSelect,
  selected,
  isNotification = false,
}) => {
  const classes = useStyles({ unread, isNotification });
  const [member] = members || [];

  const SubTitleRenderer = subTitleRenderer ?? DefaultSubTitleRenderer;

  return (
    <ListItem
      component="div"
      className={classNames(classes.root, {
        [classes.selected]: selected,
      })}
      onClick={onSelect}
    >
      <div className={classes.summarySlot}>
        {members &&
          (members.length > 1 ? (
            <AvatarGroup>
              {members
                .slice(0, 3)
                .map(({ avatar: src, fallbackColor, name }) => (
                  <Avatar
                    key={`${name}-${src}`}
                    size="lg"
                    src={src}
                    variant="circle"
                    fallbackColor={fallbackColor}
                    text={getInitials(name) || ''}
                  />
                ))}
            </AvatarGroup>
          ) : (
            <div className="relative">
              <Avatar
                size="lg"
                variant={avatarVariant === 'rounded' ? 'rounded' : 'circle'}
                src={member?.avatar || ''}
                fallbackColor={member?.fallbackColor || ''}
                text={getInitials(member?.name) || ''}
              />
              {member?.subAvatarInfo && (
                <Avatar
                  className="absolute -bottom-1 -right-1"
                  size="sm"
                  variant="rounded"
                  src={member.subAvatarInfo.avatar}
                  fallbackColor={member.subAvatarInfo.fallbackColor || ''}
                  text={getInitials(member.subAvatarInfo.name) || ''}
                />
              )}
            </div>
          ))}

        <div className={classes.titleSlot}>
          <TruncatedText
            text={title}
            styleProps={{
              ...typography13RegularStyle,
              color: BlackHeadings,
              fontWeight: unread ? 500 : 400,
            }}
          />
          <SubTitleRenderer>
            {subTitle && (
              <Body
                size="sm"
                className="text-gray-small leading-[21px] pr-[22px] flex-1 text-ellipsis overflow-hidden whitespace-nowrap"
              >
                {subTitle}
              </Body>
            )}
          </SubTitleRenderer>
        </div>
      </div>

      <div className={classes.time}>
        <div className={classes.status}>
          {unread && (
            <StatusIcon
              style={{
                color: BlackHeadings,
                fontSize: 8,
              }}
            />
          )}
        </div>
        {timestamp && (
          <ChannelTimeStamp time={timestamp} unReadNotification={unread} />
        )}
      </div>
    </ListItem>
  );
};
